/**
 * Lot definitions for the Marapa development.
 *
 * The LOTS array below is GENERATED — do not hand-edit the block between the
 * `<generated:lots>` / `</generated:lots>` markers. It is computed by
 * scripts/generate-lots.mjs from three inputs:
 *   1. the published Google Sheet dimensions (Ancho / Oeste / Este / Status),
 *   2. the surveyed corner points P2 / P11 / P1 (see src/data/refPoints.ts), and
 *   3. the street-width variable (STREET_WIDTH_M in the script, 12 m default).
 *
 * Model — anchored at the P2 corner:
 *   - EAST axis  = P2 -> P11 (top border);  SOUTH axis = P2 -> P1 (left border).
 *   - Rows stack from the top with a street between each:
 *         street / lots(C) / street / lots(B) / street / lots(A).
 *   - Within a row, lots tile EAST from the left border (width = Ancho); each
 *     lot drops SOUTH by Oeste (west corner) / Este (east corner).
 *
 * This is READ-ONLY at runtime — the app only reads the baked coordinates.
 * When the sheet or the street width changes, run `npm run generate:lots` to
 * recompute, then commit. See docs/lots-geometry.md.
 *
 * Status shown below is only the offline fallback; the live status comes from
 * the CSV Status column at runtime (see lotStatus / lotsApi.ts).
 */

export type LotStatus = 'available' | 'under-contract' | 'pending' | 'sold';
export interface LonLat { lon: number; lat: number }

export interface LotSpec {
  /** Row id in the public lots-meta sheet (A01, A02, …). Joins CSV dimensions onto the lot. */
  id: string;
  name?: string;
  status: LotStatus;
  /** True survey polygon (open ring — do NOT repeat the first vertex at the end).
   *  Source of all measurements: dimensions, coordinates, angles, area and the
   *  printable plan. Never a visually-adjusted version. */
  corners: LonLat[];
  /** Optional map-only override drawn in place of `corners` (e.g. a corner lot
   *  clipped to the perimeter, or a lot widened to close a visual gap). Purely
   *  cosmetic — the PDF and all measurements always use `corners`. */
  displayCorners?: LonLat[];
  priceUsd?: number;
  buyer?: string | null;
  notes?: string;
}

/** Per-lot dimensional metadata pulled from the published Google Sheet. */
export interface LotMeta {
  id: string;       // CSV row id, e.g. "A01"
  lot?: string;     // CSV "Lot" name (e.g. "0-4")
  ancho?: number;   // width in metres
  oeste?: number;   // west edge length in metres
  este?: number;    // east edge length in metres
  area?: number;    // area in m² (authoritative when present)
  status?: LotStatus; // CSV "Status" — overrides LotSpec.status when present
}

export const LOTS: LotSpec[] = [
  // <generated:lots> — do not edit by hand; run `npm run generate:lots`
  {
    id: 'C01',
    name: "3-8-9",
    status: 'available',
    corners: [
      { lon: -63.9933050, lat: 10.8998147 },
      { lon: -63.9927697, lat: 10.8999237 },
      { lon: -63.9925311, lat: 10.8982217 },
      { lon: -63.9930664, lat: 10.8981127 },
    ],
  },
  {
    id: 'C02',
    name: "3-8-8-4-1-1 al 3-8-8-4-11",
    status: 'available',
    corners: [
      { lon: -63.9927697, lat: 10.8999237 },
      { lon: -63.9922343, lat: 10.9000328 },
      { lon: -63.9919957, lat: 10.8983307 },
      { lon: -63.9925311, lat: 10.8982217 },
    ],
  },
  {
    id: 'C03',
    name: "O-5",
    status: 'available',
    corners: [
      { lon: -63.9922343, lat: 10.9000328 },
      { lon: -63.9919042, lat: 10.9001000 },
      { lon: -63.9916656, lat: 10.8983980 },
      { lon: -63.9919957, lat: 10.8983307 },
    ],
  },
  {
    id: 'C04',
    name: "2-5-1-1 al 2-5-1-10",
    status: 'available',
    corners: [
      { lon: -63.9919042, lat: 10.9001000 },
      { lon: -63.9917191, lat: 10.9001377 },
      { lon: -63.9914805, lat: 10.8984357 },
      { lon: -63.9916656, lat: 10.8983980 },
    ],
  },
  {
    id: 'C05',
    name: "2-4-4-1 al 2-4-4-5",
    status: 'available',
    corners: [
      { lon: -63.9917191, lat: 10.9001377 },
      { lon: -63.9914434, lat: 10.9001939 },
      { lon: -63.9912047, lat: 10.8984918 },
      { lon: -63.9914805, lat: 10.8984357 },
    ],
  },
  {
    id: 'C06',
    name: "2-4-3",
    status: 'available',
    corners: [
      { lon: -63.9914434, lat: 10.9001939 },
      { lon: -63.9911676, lat: 10.9002500 },
      { lon: -63.9909290, lat: 10.8985480 },
      { lon: -63.9912047, lat: 10.8984918 },
    ],
  },
  {
    id: 'C07',
    name: "2-4-2",
    status: 'available',
    corners: [
      { lon: -63.9911676, lat: 10.9002500 },
      { lon: -63.9908918, lat: 10.9003062 },
      { lon: -63.9906532, lat: 10.8986042 },
      { lon: -63.9909290, lat: 10.8985480 },
    ],
  },
  {
    id: 'C08',
    name: "2-4-1",
    status: 'available',
    corners: [
      { lon: -63.9908918, lat: 10.9003062 },
      { lon: -63.9906160, lat: 10.9003624 },
      { lon: -63.9903774, lat: 10.8986603 },
      { lon: -63.9906532, lat: 10.8986042 },
    ],
  },
  {
    id: 'C09',
    name: "2-3-3-1",
    status: 'available',
    corners: [
      { lon: -63.9906160, lat: 10.9003624 },
      { lon: -63.9902489, lat: 10.9004371 },
      { lon: -63.9900103, lat: 10.8987351 },
      { lon: -63.9903774, lat: 10.8986603 },
    ],
  },
  {
    id: 'C10',
    name: "2-3-2",
    status: 'available',
    corners: [
      { lon: -63.9902489, lat: 10.9004371 },
      { lon: -63.9898818, lat: 10.9005119 },
      { lon: -63.9896432, lat: 10.8988098 },
      { lon: -63.9900103, lat: 10.8987351 },
    ],
  },
  {
    id: 'C11',
    name: "2-3-1",
    status: 'available',
    corners: [
      { lon: -63.9898818, lat: 10.9005119 },
      { lon: -63.9895147, lat: 10.9005866 },
      { lon: -63.9892761, lat: 10.8988846 },
      { lon: -63.9896432, lat: 10.8988098 },
    ],
  },
  {
    id: 'C12',
    name: "2-2-5-1 al 2-2-5-11",
    status: 'available',
    corners: [
      { lon: -63.9895147, lat: 10.9005866 },
      { lon: -63.9892947, lat: 10.9006315 },
      { lon: -63.9890561, lat: 10.8989294 },
      { lon: -63.9892761, lat: 10.8988846 },
    ],
  },
  {
    id: 'C13',
    name: "2-2-4-1 al 2-2-4-9",
    status: 'available',
    corners: [
      { lon: -63.9892947, lat: 10.9006315 },
      { lon: -63.9890745, lat: 10.9006763 },
      { lon: -63.9888359, lat: 10.8989743 },
      { lon: -63.9890561, lat: 10.8989294 },
    ],
  },
  {
    id: 'C14',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9890745, lat: 10.9006763 },
      { lon: -63.9874617, lat: 10.9010048 },
      { lon: -63.9872231, lat: 10.8993027 },
      { lon: -63.9888359, lat: 10.8989743 },
    ],
  },
  {
    id: 'C15',
    name: "O-3",
    status: 'available',
    corners: [
      { lon: -63.9874617, lat: 10.9010048 },
      { lon: -63.9870845, lat: 10.9010816 },
      { lon: -63.9868459, lat: 10.8993796 },
      { lon: -63.9872231, lat: 10.8993027 },
    ],
  },
  {
    id: 'C16',
    name: "2-1-2-1 al 2-1-2-12",
    status: 'available',
    corners: [
      { lon: -63.9870845, lat: 10.9010816 },
      { lon: -63.9866258, lat: 10.9011750 },
      { lon: -63.9863872, lat: 10.8994730 },
      { lon: -63.9868459, lat: 10.8993796 },
    ],
  },
  {
    id: 'C17',
    name: "2-1-1-1",
    status: 'available',
    corners: [
      { lon: -63.9866258, lat: 10.9011750 },
      { lon: -63.9862587, lat: 10.9012498 },
      { lon: -63.9860201, lat: 10.8995477 },
      { lon: -63.9863872, lat: 10.8994730 },
    ],
  },
  {
    id: 'C18',
    name: "1-4-4",
    status: 'available',
    corners: [
      { lon: -63.9862587, lat: 10.9012498 },
      { lon: -63.9857769, lat: 10.9013479 },
      { lon: -63.9855383, lat: 10.8996459 },
      { lon: -63.9860201, lat: 10.8995477 },
    ],
  },
  {
    id: 'C19',
    name: "1-4-3",
    status: 'available',
    corners: [
      { lon: -63.9857769, lat: 10.9013479 },
      { lon: -63.9852950, lat: 10.9014460 },
      { lon: -63.9850564, lat: 10.8997440 },
      { lon: -63.9855383, lat: 10.8996459 },
    ],
  },
  {
    id: 'C20',
    name: "1-4-2",
    status: 'available',
    corners: [
      { lon: -63.9852950, lat: 10.9014460 },
      { lon: -63.9848132, lat: 10.9015442 },
      { lon: -63.9845746, lat: 10.8998421 },
      { lon: -63.9850564, lat: 10.8997440 },
    ],
  },
  {
    id: 'C21',
    name: "1-4-1",
    status: 'available',
    corners: [
      { lon: -63.9848132, lat: 10.9015442 },
      { lon: -63.9843313, lat: 10.9016423 },
      { lon: -63.9840927, lat: 10.8999403 },
      { lon: -63.9845746, lat: 10.8998421 },
    ],
  },
  {
    id: 'C22',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9843313, lat: 10.9016423 },
      { lon: -63.9836683, lat: 10.9017774 },
      { lon: -63.9834297, lat: 10.9000753 },
      { lon: -63.9840927, lat: 10.8999403 },
    ],
  },
  {
    id: 'C23',
    name: "3-1-4",
    status: 'available',
    corners: [
      { lon: -63.9836683, lat: 10.9017774 },
      { lon: -63.9819422, lat: 10.9021289 },
      { lon: -63.9817035, lat: 10.9004269 },
      { lon: -63.9834297, lat: 10.9000753 },
    ],
  },
  {
    id: 'C24',
    name: "2-1-3-8-1",
    status: 'available',
    corners: [
      { lon: -63.9819422, lat: 10.9021289 },
      { lon: -63.9814210, lat: 10.9022351 },
      { lon: -63.9811824, lat: 10.9005330 },
      { lon: -63.9817035, lat: 10.9004269 },
    ],
  },
  {
    id: 'C25',
    name: "3-6-2-1 al 3-6-2-9",
    status: 'available',
    corners: [
      { lon: -63.9814210, lat: 10.9022351 },
      { lon: -63.9809550, lat: 10.9023299 },
      { lon: -63.9807164, lat: 10.9006279 },
      { lon: -63.9811824, lat: 10.9005330 },
    ],
  },
  {
    id: 'C26',
    name: "3-6-1",
    status: 'available',
    corners: [
      { lon: -63.9809550, lat: 10.9023299 },
      { lon: -63.9804844, lat: 10.9024258 },
      { lon: -63.9802458, lat: 10.9007238 },
      { lon: -63.9807164, lat: 10.9006279 },
    ],
  },
  {
    id: 'C27',
    name: "3-6-3-1 al 3-6-3-10",
    status: 'available',
    corners: [
      { lon: -63.9804844, lat: 10.9024258 },
      { lon: -63.9801260, lat: 10.9024988 },
      { lon: -63.9798874, lat: 10.9007967 },
      { lon: -63.9802458, lat: 10.9007238 },
    ],
  },
  {
    id: 'C28',
    name: "3-7-1",
    status: 'available',
    corners: [
      { lon: -63.9801260, lat: 10.9024988 },
      { lon: -63.9795239, lat: 10.9026214 },
      { lon: -63.9792853, lat: 10.9009194 },
      { lon: -63.9798874, lat: 10.9007967 },
    ],
  },
  {
    id: 'C29',
    name: "3-8-1 al 3-8-7",
    status: 'available',
    corners: [
      { lon: -63.9795239, lat: 10.9026214 },
      { lon: -63.9793268, lat: 10.9026616 },
      { lon: -63.9790882, lat: 10.9009595 },
      { lon: -63.9792853, lat: 10.9009194 },
    ],
  },
  {
    id: 'C30',
    name: "3-7-2-1 al 3-7-2-8",
    status: 'available',
    corners: [
      { lon: -63.9793268, lat: 10.9026616 },
      { lon: -63.9791815, lat: 10.9026911 },
      { lon: -63.9789429, lat: 10.9009891 },
      { lon: -63.9790882, lat: 10.9009595 },
    ],
  },
  {
    id: 'C31',
    name: "3-4-1 al 3-4-10-5",
    status: 'available',
    corners: [
      { lon: -63.9791815, lat: 10.9026911 },
      { lon: -63.9787964, lat: 10.9027696 },
      { lon: -63.9785578, lat: 10.9010675 },
      { lon: -63.9789429, lat: 10.9009891 },
    ],
  },
  {
    id: 'C32',
    name: "3-5-1 al 3-5-8",
    status: 'available',
    corners: [
      { lon: -63.9787964, lat: 10.9027696 },
      { lon: -63.9784696, lat: 10.9028361 },
      { lon: -63.9782310, lat: 10.9011341 },
      { lon: -63.9785578, lat: 10.9010675 },
    ],
  },
  {
    id: 'C33',
    name: "2-7-1-1 al 2-7-1-4-11",
    status: 'available',
    corners: [
      { lon: -63.9784696, lat: 10.9028361 },
      { lon: -63.9780960, lat: 10.9029122 },
      { lon: -63.9778574, lat: 10.9012102 },
      { lon: -63.9782310, lat: 10.9011341 },
    ],
  },
  {
    id: 'C34',
    name: "O-1",
    status: 'available',
    corners: [
      { lon: -63.9780960, lat: 10.9029122 },
      { lon: -63.9768953, lat: 10.9031568 },
      { lon: -63.9757552, lat: 10.9003484 },
      { lon: -63.9776238, lat: 10.8995440 },
    ],
    displayCorners: [
      { lon: -63.9780960, lat: 10.9029122 },
      { lon: -63.9770971, lat: 10.9031157 },
      { lon: -63.9759675, lat: 10.9002570 },
      { lon: -63.9776238, lat: 10.8995440 },
    ],
  },
  {
    id: 'B01',
    name: "O-1-1",
    status: 'available',
    corners: [
      { lon: -63.9930514, lat: 10.8980052 },
      { lon: -63.9926289, lat: 10.8980912 },
      { lon: -63.9923777, lat: 10.8962996 },
      { lon: -63.9928002, lat: 10.8962135 },
    ],
  },
  {
    id: 'B02',
    name: "O-1",
    status: 'available',
    corners: [
      { lon: -63.9926289, lat: 10.8980912 },
      { lon: -63.9923883, lat: 10.8981402 },
      { lon: -63.9921372, lat: 10.8963486 },
      { lon: -63.9923777, lat: 10.8962996 },
    ],
  },
  {
    id: 'B03',
    name: "O-12",
    status: 'available',
    corners: [
      { lon: -63.9923883, lat: 10.8981402 },
      { lon: -63.9922091, lat: 10.8981767 },
      { lon: -63.9919580, lat: 10.8963851 },
      { lon: -63.9921372, lat: 10.8963486 },
    ],
  },
  {
    id: 'B04',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9922091, lat: 10.8981767 },
      { lon: -63.9919224, lat: 10.8982351 },
      { lon: -63.9916713, lat: 10.8964435 },
      { lon: -63.9919580, lat: 10.8963851 },
    ],
  },
  {
    id: 'B05',
    name: "3-3-6",
    status: 'available',
    corners: [
      { lon: -63.9919224, lat: 10.8982351 },
      { lon: -63.9914500, lat: 10.8983313 },
      { lon: -63.9911988, lat: 10.8965397 },
      { lon: -63.9916713, lat: 10.8964435 },
    ],
  },
  {
    id: 'B06',
    name: "2-2-1 al 2-2-2",
    status: 'available',
    corners: [
      { lon: -63.9914500, lat: 10.8983313 },
      { lon: -63.9910315, lat: 10.8984165 },
      { lon: -63.9907804, lat: 10.8966249 },
      { lon: -63.9911988, lat: 10.8965397 },
    ],
  },
  {
    id: 'B07',
    name: "3-7-2-1 al 3-7-2-8",
    status: 'available',
    corners: [
      { lon: -63.9910315, lat: 10.8984165 },
      { lon: -63.9906250, lat: 10.8984993 },
      { lon: -63.9903739, lat: 10.8967077 },
      { lon: -63.9907804, lat: 10.8966249 },
    ],
  },
  {
    id: 'B08',
    name: "2-2-3-1 al 2-2-3-5",
    status: 'available',
    corners: [
      { lon: -63.9906250, lat: 10.8984993 },
      { lon: -63.9904159, lat: 10.8985419 },
      { lon: -63.9901647, lat: 10.8967503 },
      { lon: -63.9903739, lat: 10.8967077 },
    ],
  },
  {
    id: 'B09',
    name: "2-1-3-1 al 2-1-3-8",
    status: 'available',
    corners: [
      { lon: -63.9904159, lat: 10.8985419 },
      { lon: -63.9901507, lat: 10.8985959 },
      { lon: -63.9898995, lat: 10.8968043 },
      { lon: -63.9901647, lat: 10.8967503 },
    ],
  },
  {
    id: 'B10',
    name: "0-5",
    status: 'available',
    corners: [
      { lon: -63.9901507, lat: 10.8985959 },
      { lon: -63.9898413, lat: 10.8986589 },
      { lon: -63.9895901, lat: 10.8968673 },
      { lon: -63.9898995, lat: 10.8968043 },
    ],
  },
  {
    id: 'B11',
    name: "2-5-2-1 al 2-5-2-7",
    status: 'available',
    corners: [
      { lon: -63.9898413, lat: 10.8986589 },
      { lon: -63.9896670, lat: 10.8986944 },
      { lon: -63.9894159, lat: 10.8969028 },
      { lon: -63.9895901, lat: 10.8968673 },
    ],
  },
  {
    id: 'B12',
    name: "2-5-3-1 al 2-5-3-6",
    status: 'available',
    corners: [
      { lon: -63.9896670, lat: 10.8986944 },
      { lon: -63.9894928, lat: 10.8987299 },
      { lon: -63.9892416, lat: 10.8969383 },
      { lon: -63.9894159, lat: 10.8969028 },
    ],
  },
  {
    id: 'B13',
    name: "2-5-4 al 2-5-5",
    status: 'available',
    corners: [
      { lon: -63.9894928, lat: 10.8987299 },
      { lon: -63.9891441, lat: 10.8988009 },
      { lon: -63.9888929, lat: 10.8970093 },
      { lon: -63.9892416, lat: 10.8969383 },
    ],
  },
  {
    id: 'B14',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9891441, lat: 10.8988009 },
      { lon: -63.9890455, lat: 10.8988210 },
      { lon: -63.9887943, lat: 10.8970294 },
      { lon: -63.9888929, lat: 10.8970093 },
    ],
  },
  {
    id: 'B15',
    name: "2-5-6-1 al 2-5-6-3",
    status: 'available',
    corners: [
      { lon: -63.9890455, lat: 10.8988210 },
      { lon: -63.9888711, lat: 10.8988565 },
      { lon: -63.9886200, lat: 10.8970649 },
      { lon: -63.9887943, lat: 10.8970294 },
    ],
  },
  {
    id: 'B16',
    name: "2-6-1 al 2-6-4-1",
    status: 'available',
    corners: [
      { lon: -63.9888711, lat: 10.8988565 },
      { lon: -63.9877263, lat: 10.8990897 },
      { lon: -63.9874752, lat: 10.8972981 },
      { lon: -63.9886200, lat: 10.8970649 },
    ],
  },
  {
    id: 'B17',
    name: "2-7-1-1 al 2-7-1-4-11",
    status: 'available',
    corners: [
      { lon: -63.9877263, lat: 10.8990897 },
      { lon: -63.9866801, lat: 10.8993028 },
      { lon: -63.9864289, lat: 10.8975111 },
      { lon: -63.9874752, lat: 10.8972981 },
    ],
  },
  {
    id: 'B18',
    name: "1-1-5-1 al 1-1-5-3",
    status: 'available',
    corners: [
      { lon: -63.9866801, lat: 10.8993028 },
      { lon: -63.9862321, lat: 10.8993940 },
      { lon: -63.9859809, lat: 10.8976024 },
      { lon: -63.9864289, lat: 10.8975111 },
    ],
  },
  {
    id: 'B19',
    name: "3-1-1 al 3-1-3 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9862321, lat: 10.8993940 },
      { lon: -63.9855455, lat: 10.8995338 },
      { lon: -63.9852944, lat: 10.8977422 },
      { lon: -63.9859809, lat: 10.8976024 },
    ],
  },
  {
    id: 'B20',
    name: "3-2-1 al 3-2-4",
    status: 'available',
    corners: [
      { lon: -63.9855455, lat: 10.8995338 },
      { lon: -63.9853014, lat: 10.8995835 },
      { lon: -63.9850503, lat: 10.8977919 },
      { lon: -63.9852944, lat: 10.8977422 },
    ],
  },
  {
    id: 'B21',
    name: "O-4 (DECIMO SEXTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9853014, lat: 10.8995835 },
      { lon: -63.9849985, lat: 10.8996452 },
      { lon: -63.9847474, lat: 10.8978536 },
      { lon: -63.9850503, lat: 10.8977919 },
    ],
  },
  {
    id: 'B22',
    name: "3-2-5 al 3-2-15-3",
    status: 'available',
    corners: [
      { lon: -63.9849985, lat: 10.8996452 },
      { lon: -63.9841595, lat: 10.8998161 },
      { lon: -63.9839084, lat: 10.8980245 },
      { lon: -63.9847474, lat: 10.8978536 },
    ],
  },
  {
    id: 'B23',
    name: "3-4-1 al 3-4-10-5",
    status: 'available',
    corners: [
      { lon: -63.9841595, lat: 10.8998161 },
      { lon: -63.9832442, lat: 10.9000025 },
      { lon: -63.9829930, lat: 10.8982109 },
      { lon: -63.9839084, lat: 10.8980245 },
    ],
  },
  {
    id: 'B24',
    name: "3-5-1 al 3-5-8",
    status: 'available',
    corners: [
      { lon: -63.9832442, lat: 10.9000025 },
      { lon: -63.9823287, lat: 10.9001890 },
      { lon: -63.9820776, lat: 10.8983973 },
      { lon: -63.9829930, lat: 10.8982109 },
    ],
  },
  {
    id: 'B25',
    name: "1-3-1 al 1-3-10-3",
    status: 'available',
    corners: [
      { lon: -63.9823287, lat: 10.9001890 },
      { lon: -63.9811448, lat: 10.9004301 },
      { lon: -63.9808936, lat: 10.8986385 },
      { lon: -63.9820776, lat: 10.8983973 },
    ],
  },
  {
    id: 'B26',
    name: "O-9",
    status: 'available',
    corners: [
      { lon: -63.9811448, lat: 10.9004301 },
      { lon: -63.9810731, lat: 10.9004447 },
      { lon: -63.9808220, lat: 10.8986531 },
      { lon: -63.9808936, lat: 10.8986385 },
    ],
  },
  {
    id: 'B27',
    name: "3-8-9 (CUARTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9810731, lat: 10.9004447 },
      { lon: -63.9809351, lat: 10.9004728 },
      { lon: -63.9806925, lat: 10.8987417 },
      { lon: -63.9808220, lat: 10.8986531 },
    ],
  },
  {
    id: 'B28',
    name: "2-4-1 al 2-4-3 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9809351, lat: 10.9004728 },
      { lon: -63.9805678, lat: 10.9005476 },
      { lon: -63.9803568, lat: 10.8990427 },
      { lon: -63.9806925, lat: 10.8987417 },
    ],
  },
  {
    id: 'B29',
    name: "1-2-1 al 1-2-5",
    status: 'available',
    corners: [
      { lon: -63.9805678, lat: 10.9005476 },
      { lon: -63.9801108, lat: 10.9006407 },
      { lon: -63.9799174, lat: 10.8992611 },
      { lon: -63.9803669, lat: 10.8991143 },
    ],
  },
  {
    id: 'B30',
    name: "1-1-1 (CUARTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9801108, lat: 10.9006407 },
      { lon: -63.9799406, lat: 10.9006753 },
      { lon: -63.9797648, lat: 10.8994212 },
      { lon: -63.9799174, lat: 10.8992611 },
    ],
  },
  {
    id: 'B31',
    name: "1-1-2 (CUARTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9799406, lat: 10.9006753 },
      { lon: -63.9797728, lat: 10.9007095 },
      { lon: -63.9795869, lat: 10.8993837 },
      { lon: -63.9797547, lat: 10.8993495 },
    ],
  },
  {
    id: 'B32',
    name: "1-1-3 (CUARTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9797728, lat: 10.9007095 },
      { lon: -63.9796050, lat: 10.9007437 },
      { lon: -63.9794191, lat: 10.8994179 },
      { lon: -63.9795869, lat: 10.8993837 },
    ],
  },
  {
    id: 'B33',
    name: "1-1-4",
    status: 'available',
    corners: [
      { lon: -63.9796050, lat: 10.9007437 },
      { lon: -63.9794371, lat: 10.9007779 },
      { lon: -63.9792500, lat: 10.8994431 },
      { lon: -63.9794191, lat: 10.8994179 },
    ],
  },
  {
    id: 'B34',
    name: "3-8-8-4-1-1 al 3-8-8-4-11",
    status: 'available',
    corners: [
      { lon: -63.9794371, lat: 10.9007779 },
      { lon: -63.9792518, lat: 10.9008156 },
      { lon: -63.9790647, lat: 10.8994809 },
      { lon: -63.9792500, lat: 10.8994431 },
    ],
  },
  {
    id: 'B35',
    name: "3-2-1 al 3-2-4",
    status: 'available',
    corners: [
      { lon: -63.9792518, lat: 10.9008156 },
      { lon: -63.9791407, lat: 10.9008382 },
      { lon: -63.9789335, lat: 10.8993602 },
      { lon: -63.9790446, lat: 10.8993375 },
    ],
  },
  {
    id: 'B36',
    name: "3-2-5 al 3-2-15-3",
    status: 'available',
    corners: [
      { lon: -63.9791407, lat: 10.9008382 },
      { lon: -63.9788648, lat: 10.9008944 },
      { lon: -63.9786576, lat: 10.8994164 },
      { lon: -63.9789335, lat: 10.8993602 },
    ],
  },
  {
    id: 'B37',
    name: "3-1-1 al 3-1-3",
    status: 'available',
    corners: [
      { lon: -63.9788648, lat: 10.9008944 },
      { lon: -63.9785825, lat: 10.9009519 },
      { lon: -63.9783752, lat: 10.8994739 },
      { lon: -63.9786576, lat: 10.8994164 },
    ],
  },
  {
    id: 'B38',
    name: "2-6-1 al 2-6-4-1",
    status: 'available',
    corners: [
      { lon: -63.9785825, lat: 10.9009519 },
      { lon: -63.9781479, lat: 10.9010405 },
      { lon: -63.9779344, lat: 10.8995176 },
      { lon: -63.9783752, lat: 10.8994739 },
    ],
  },
  {
    id: 'B39',
    name: "1-1-5-1 al 1-1-5-3",
    status: 'available',
    corners: [
      { lon: -63.9781479, lat: 10.9010405 },
      { lon: -63.9780566, lat: 10.9010590 },
      { lon: -63.9778431, lat: 10.8995362 },
      { lon: -63.9779344, lat: 10.8995176 },
    ],
  },
  {
    id: 'B40',
    name: "2-5-4 al 2-5-5",
    status: 'available',
    corners: [
      { lon: -63.9780566, lat: 10.9010590 },
      { lon: -63.9779175, lat: 10.9010874 },
      { lon: -63.9777040, lat: 10.8995645 },
      { lon: -63.9778431, lat: 10.8995362 },
    ],
    displayCorners: [
      { lon: -63.9780566, lat: 10.9010590 },
      { lon: -63.9778423, lat: 10.9011027 },
      { lon: -63.9776288, lat: 10.8995798 },
      { lon: -63.9778431, lat: 10.8995362 },
    ],
  },
  {
    id: 'A01',
    name: "O-4 (VIGESIMO LOTE)",
    status: 'sold',
    corners: [
      { lon: -63.9927851, lat: 10.8961061 },
      { lon: -63.9922296, lat: 10.8962192 },
      { lon: -63.9915640, lat: 10.8914714 },
      { lon: -63.9920065, lat: 10.8905520 },
    ],
  },
  {
    id: 'A02',
    name: "O-1 (TERCER LOTE)",
    status: 'under-contract',
    corners: [
      { lon: -63.9922296, lat: 10.8962192 },
      { lon: -63.9916741, lat: 10.8963323 },
      { lon: -63.9910085, lat: 10.8915845 },
      { lon: -63.9914950, lat: 10.8909787 },
    ],
  },
  {
    id: 'A03',
    name: "O-4-5",
    status: 'sold',
    corners: [
      { lon: -63.9916741, lat: 10.8963323 },
      { lon: -63.9913426, lat: 10.8963998 },
      { lon: -63.9907838, lat: 10.8924135 },
      { lon: -63.9910085, lat: 10.8915845 },
    ],
  },
  {
    id: 'A04',
    name: "O-2",
    status: 'sold',
    corners: [
      { lon: -63.9913426, lat: 10.8963998 },
      { lon: -63.9910738, lat: 10.8964546 },
      { lon: -63.9905966, lat: 10.8930505 },
      { lon: -63.9907838, lat: 10.8924135 },
    ],
  },
  {
    id: 'A05',
    name: "O-4 (DECIMO NOVENO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9910738, lat: 10.8964546 },
      { lon: -63.9908319, lat: 10.8965039 },
      { lon: -63.9903547, lat: 10.8930998 },
      { lon: -63.9905966, lat: 10.8930505 },
    ],
  },
  {
    id: 'A06',
    name: "O-10",
    status: 'sold',
    corners: [
      { lon: -63.9908319, lat: 10.8965039 },
      { lon: -63.9906437, lat: 10.8965422 },
      { lon: -63.9902544, lat: 10.8937652 },
      { lon: -63.9904237, lat: 10.8935925 },
    ],
  },
  {
    id: 'A07',
    name: "1-1-1 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9906437, lat: 10.8965422 },
      { lon: -63.9904086, lat: 10.8965901 },
      { lon: -63.9900319, lat: 10.8939026 },
      { lon: -63.9902544, lat: 10.8937652 },
    ],
  },
  {
    id: 'A08',
    name: "1-1-2 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9904086, lat: 10.8965901 },
      { lon: -63.9901641, lat: 10.8966399 },
      { lon: -63.9898250, lat: 10.8942212 },
      { lon: -63.9900319, lat: 10.8939026 },
    ],
  },
  {
    id: 'A09',
    name: "1-1-3 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9901641, lat: 10.8966399 },
      { lon: -63.9898942, lat: 10.8966948 },
      { lon: -63.9895677, lat: 10.8943657 },
      { lon: -63.9898250, lat: 10.8942212 },
    ],
  },
  {
    id: 'A10',
    name: "1-1-4 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9898942, lat: 10.8966948 },
      { lon: -63.9896140, lat: 10.8967519 },
      { lon: -63.9893000, lat: 10.8945124 },
      { lon: -63.9895677, lat: 10.8943657 },
    ],
  },
  {
    id: 'A11',
    name: "O-8 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9896140, lat: 10.8967519 },
      { lon: -63.9893907, lat: 10.8967974 },
      { lon: -63.9891144, lat: 10.8948266 },
      { lon: -63.9893000, lat: 10.8945124 },
    ],
  },
  {
    id: 'A12',
    name: "O-7 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9893907, lat: 10.8967974 },
      { lon: -63.9891369, lat: 10.8968491 },
      { lon: -63.9889034, lat: 10.8951828 },
      { lon: -63.9891144, lat: 10.8948266 },
    ],
  },
  {
    id: 'A13',
    name: "O-6 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9891369, lat: 10.8968491 },
      { lon: -63.9888368, lat: 10.8969102 },
      { lon: -63.9886484, lat: 10.8955665 },
      { lon: -63.9889034, lat: 10.8951828 },
    ],
  },
  {
    id: 'A14',
    name: "O-4 (DECIMO OCTAVO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9888368, lat: 10.8969102 },
      { lon: -63.9883888, lat: 10.8970014 },
      { lon: -63.9882004, lat: 10.8956577 },
      { lon: -63.9886484, lat: 10.8955665 },
    ],
  },
  {
    id: 'A15',
    name: "1-2-1 (PRIMER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9883888, lat: 10.8970014 },
      { lon: -63.9881200, lat: 10.8970562 },
      { lon: -63.9878249, lat: 10.8949510 },
      { lon: -63.9880937, lat: 10.8948963 },
    ],
  },
  {
    id: 'A16',
    name: "1-2-2 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9881200, lat: 10.8970562 },
      { lon: -63.9878512, lat: 10.8971109 },
      { lon: -63.9875561, lat: 10.8950058 },
      { lon: -63.9878249, lat: 10.8949510 },
    ],
  },
  {
    id: 'A17',
    name: "1-2-3 (PRIMER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9878512, lat: 10.8971109 },
      { lon: -63.9875824, lat: 10.8971657 },
      { lon: -63.9872873, lat: 10.8950605 },
      { lon: -63.9875561, lat: 10.8950058 },
    ],
  },
  {
    id: 'A18',
    name: "1-2-4 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9875824, lat: 10.8971657 },
      { lon: -63.9873136, lat: 10.8972204 },
      { lon: -63.9870185, lat: 10.8951152 },
      { lon: -63.9872873, lat: 10.8950605 },
    ],
  },
  {
    id: 'A19',
    name: "1-2-5 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9873136, lat: 10.8972204 },
      { lon: -63.9870448, lat: 10.8972751 },
      { lon: -63.9867497, lat: 10.8951700 },
      { lon: -63.9870185, lat: 10.8951152 },
    ],
  },
  {
    id: 'A20',
    name: "1-2-6-1 al 1-2-6-3  (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9870448, lat: 10.8972751 },
      { lon: -63.9867760, lat: 10.8973299 },
      { lon: -63.9864809, lat: 10.8952247 },
      { lon: -63.9867497, lat: 10.8951700 },
    ],
  },
  {
    id: 'A21',
    name: "1-2-7-1 al 1-2-7-3 (PRIMER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9867760, lat: 10.8973299 },
      { lon: -63.9865072, lat: 10.8973846 },
      { lon: -63.9862121, lat: 10.8952795 },
      { lon: -63.9864809, lat: 10.8952247 },
    ],
  },
  {
    id: 'A22',
    name: "1-2-8-1 al 1-2-8-8 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9865072, lat: 10.8973846 },
      { lon: -63.9862384, lat: 10.8974394 },
      { lon: -63.9859433, lat: 10.8953342 },
      { lon: -63.9862121, lat: 10.8952795 },
    ],
  },
  {
    id: 'A23',
    name: "3-3-1, 3-3-4, 3-3-5 y 3-3-7",
    status: 'available',
    corners: [
      { lon: -63.9862384, lat: 10.8974394 },
      { lon: -63.9856381, lat: 10.8975616 },
      { lon: -63.9853430, lat: 10.8954565 },
      { lon: -63.9859433, lat: 10.8953342 },
    ],
  },
  {
    id: 'A24',
    name: "3-1-4 (QUINTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9856381, lat: 10.8975616 },
      { lon: -63.9853872, lat: 10.8976127 },
      { lon: -63.9851361, lat: 10.8958211 },
      { lon: -63.9853870, lat: 10.8957700 },
    ],
  },
  {
    id: 'A25',
    name: "3-3-6 (SEPTIMO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9853872, lat: 10.8976127 },
      { lon: -63.9851364, lat: 10.8976638 },
      { lon: -63.9848727, lat: 10.8957826 },
      { lon: -63.9851361, lat: 10.8958211 },
    ],
  },
  {
    id: 'A26',
    name: "2-2-1 al 2-2-2 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9851364, lat: 10.8976638 },
      { lon: -63.9849944, lat: 10.8976927 },
      { lon: -63.9847433, lat: 10.8959011 },
      { lon: -63.9848852, lat: 10.8958722 },
    ],
  },
  {
    id: 'A27',
    name: "O-4 (DECIMO SEPTIMO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9849944, lat: 10.8976927 },
      { lon: -63.9848959, lat: 10.8977128 },
      { lon: -63.9846573, lat: 10.8960108 },
      { lon: -63.9847558, lat: 10.8959907 },
    ],
  },
  {
    id: 'A28',
    name: "2-5-3-1 al 2-5-3-6 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9848959, lat: 10.8977128 },
      { lon: -63.9847973, lat: 10.8977329 },
      { lon: -63.9846215, lat: 10.8964787 },
      { lon: -63.9847201, lat: 10.8964587 },
    ],
  },
  {
    id: 'A29',
    name: "2-5-2-1 al 2-5-2-7 (CUARTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9847973, lat: 10.8977329 },
      { lon: -63.9846988, lat: 10.8977529 },
      { lon: -63.9845230, lat: 10.8964988 },
      { lon: -63.9846215, lat: 10.8964787 },
    ],
  },
  {
    id: 'A30',
    name: "2-5-1-1 al 2-5-1-10 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9846988, lat: 10.8977529 },
      { lon: -63.9846002, lat: 10.8977730 },
      { lon: -63.9844244, lat: 10.8965189 },
      { lon: -63.9845230, lat: 10.8964988 },
    ],
  },
  {
    id: 'A31',
    name: "2-4-4-1 al 2-4-4-5",
    status: 'available',
    corners: [
      { lon: -63.9846002, lat: 10.8977730 },
      { lon: -63.9844748, lat: 10.8977986 },
      { lon: -63.9842990, lat: 10.8965444 },
      { lon: -63.9844244, lat: 10.8965189 },
    ],
  },
  {
    id: 'A32',
    name: "2-3-1 AL 2-3-3-1 (SEGUNDO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9844748, lat: 10.8977986 },
      { lon: -63.9840268, lat: 10.8978898 },
      { lon: -63.9838510, lat: 10.8966357 },
      { lon: -63.9842990, lat: 10.8965444 },
    ],
  },
  {
    id: 'A33',
    name: "2-2-5-1 al 2-2-5-11 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9840268, lat: 10.8978898 },
      { lon: -63.9839282, lat: 10.8979099 },
      { lon: -63.9837524, lat: 10.8966557 },
      { lon: -63.9838510, lat: 10.8966357 },
    ],
  },
  {
    id: 'A34',
    name: "2-5-6-1 al 2-5-6-3 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9839282, lat: 10.8979099 },
      { lon: -63.9838297, lat: 10.8979299 },
      { lon: -63.9836539, lat: 10.8966758 },
      { lon: -63.9837524, lat: 10.8966557 },
    ],
  },
  {
    id: 'A35',
    name: "2-1-3-1 al 2-1-3-8 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9838297, lat: 10.8979299 },
      { lon: -63.9837305, lat: 10.8979501 },
      { lon: -63.9835547, lat: 10.8966960 },
      { lon: -63.9836539, lat: 10.8966758 },
    ],
  },
  {
    id: 'A36',
    name: "2-2-4-1 al 2-2-4-9 (TERCER LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9837305, lat: 10.8979501 },
      { lon: -63.9836291, lat: 10.8979708 },
      { lon: -63.9834533, lat: 10.8967167 },
      { lon: -63.9835547, lat: 10.8966960 },
    ],
  },
  {
    id: 'A37',
    name: "2-2-3-1 al 2-2-3-5",
    status: 'available',
    corners: [
      { lon: -63.9836291, lat: 10.8979708 },
      { lon: -63.9835345, lat: 10.8979901 },
      { lon: -63.9833462, lat: 10.8966463 },
      { lon: -63.9834408, lat: 10.8966271 },
    ],
  },
  {
    id: 'A38',
    name: "2-1-2-1 al 2-1-2-12 (CUARTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9835345, lat: 10.8979901 },
      { lon: -63.9834163, lat: 10.8980141 },
      { lon: -63.9832405, lat: 10.8967600 },
      { lon: -63.9833587, lat: 10.8967359 },
    ],
  },
  {
    id: 'A39',
    name: "2-1-1-1 (CUARTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9834163, lat: 10.8980141 },
      { lon: -63.9832474, lat: 10.8980485 },
      { lon: -63.9830716, lat: 10.8967944 },
      { lon: -63.9832405, lat: 10.8967600 },
    ],
  },
  {
    id: 'A40',
    name: "O-5 (QUINTO LOTE)",
    status: 'available',
    corners: [
      { lon: -63.9832474, lat: 10.8980485 },
      { lon: -63.9830347, lat: 10.8980919 },
      { lon: -63.9828588, lat: 10.8968377 },
      { lon: -63.9830716, lat: 10.8967944 },
    ],
  },
  {
    id: 'A41',
    name: "1-4-1 al 1-4-4",
    status: 'available',
    corners: [
      { lon: -63.9830347, lat: 10.8980919 },
      { lon: -63.9807947, lat: 10.8985481 },
      { lon: -63.9807947, lat: 10.8985481 },
      { lon: -63.9828588, lat: 10.8968377 },
    ],
  },
  // </generated:lots>
];

export function getAllLotPolygons() {
  // Map draws displayCorners when present (cosmetic), else the true corners.
  return LOTS.map((lot) => ({ lot, corners: lot.displayCorners ?? lot.corners }));
}

/** Polygon area in m² using equirectangular projection at the centroid. */
export function computeAreaM2(corners: LonLat[]): number {
  if (corners.length < 3) return 0;
  const meanLat = corners.reduce((s, c) => s + c.lat, 0) / corners.length;
  const cosLat = Math.cos((meanLat * Math.PI) / 180);
  const M_PER_DEG_LON = 111320 * cosLat;
  const M_PER_DEG_LAT_LOCAL = 110574;
  // Shoelace on x = lon*M_PER_DEG_LON, y = lat*M_PER_DEG_LAT
  let twiceArea = 0;
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const ax = a.lon * M_PER_DEG_LON;
    const ay = a.lat * M_PER_DEG_LAT_LOCAL;
    const bx = b.lon * M_PER_DEG_LON;
    const by = b.lat * M_PER_DEG_LAT_LOCAL;
    twiceArea += ax * by - bx * ay;
  }
  return Math.abs(twiceArea) / 2;
}

// --- Selected-lot pub-sub store ---
type SelectedLotListener = (lot: LotSpec | null) => void;
let _selected: LotSpec | null = null;
const _listeners = new Set<SelectedLotListener>();
export const selectedLotStore = {
  get: () => _selected,
  set(lot: LotSpec | null) {
    if (_selected === lot) return;
    _selected = lot;
    for (const cb of _listeners) cb(lot);
  },
  subscribe(cb: SelectedLotListener) {
    _listeners.add(cb);
    cb(_selected);
    return () => _listeners.delete(cb);
  },
};

export function findLotById(id: string): LotSpec | null {
  return LOTS.find((l) => l.id === id) ?? null;
}

/** Human-facing label combining the lot id and its internal name, e.g.
 *  `A01 (0-4)`. Falls back to the id alone when no name is set. */
export function lotLabel(lot: LotSpec): string {
  if (lot.name) return `${lot.id} (${lot.name})`;
  return lot.id;
}

// --- Lot-metadata pub-sub store ---
// Loaded from the published CSV on app start (see src/data/lotsApi.ts).
// Keyed by lot id (matches LotSpec.id, which equals the CSV row id).
type LotMetaListener = (meta: ReadonlyMap<string, LotMeta>) => void;
const _lotMeta = new Map<string, LotMeta>();
const _metaListeners = new Set<LotMetaListener>();
export const lotMetaStore = {
  get: () => _lotMeta as ReadonlyMap<string, LotMeta>,
  getById: (id: string | undefined) =>
    id ? _lotMeta.get(id) : undefined,
  setAll(entries: Iterable<readonly [string, LotMeta]>) {
    _lotMeta.clear();
    for (const [k, v] of entries) _lotMeta.set(k, v);
    for (const cb of _metaListeners) cb(_lotMeta);
  },
  subscribe(cb: LotMetaListener) {
    _metaListeners.add(cb);
    cb(_lotMeta);
    return () => _metaListeners.delete(cb);
  },
};

/** Resolve a lot's effective status: the CSV-published value if loaded,
 *  otherwise the hardcoded fallback on the LotSpec. */
export function lotStatus(lot: LotSpec): LotStatus {
  return lotMetaStore.getById(lot.id)?.status ?? lot.status;
}
